import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useGetAllSessions } from '~entities/session/api/get-sessions';
import { type StudioDetails } from '~entities/studios/model/types';
import type { UserWithRole } from '~entities/users';
import { useGetCurrentUser } from '~entities/users/api/get-current-user';
import { useUpdateSelectedSession } from '~entities/users/api/update-selected-session';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import {
  ALL_SESSIONS_ID,
  ALL_SESSIONS_OPTION,
} from '~features/new-dashboard/constants';
import { useDashboardSessionScope } from '~features/new-dashboard/hooks/use-dashboard-session-scope';
import {
  type SessionOption,
  SessionSelector,
} from '~features/shared/session-selector';

import { AttendanceDrilldownModal } from '~pages/dashboard/attendance-drilldown-modal';
import { SectionHeader } from '~pages/dashboard/section-header';

import { AccountReceivableModal } from './account-receivable-model';
import { ClassPerformanceTabs } from './class-performance-tabs';
import { DeferredMount } from './deferred-mount';
import { EnrollmentGrowthChart } from './enrollment-growth-chart';
import { LazyDashboardSection } from './lazy-dashboard-section';
import { MainLayout } from './main-layout';
import { PaymentHealthChart } from './payment-health-chart';
import { QuickActionsToolbar } from './quick-actions-toolbar';
import { RevenueTrendChart } from './revenue-trend-chart';
import { StatsRow } from './stats-row';
import { StudentRequiringAttentionSection } from './student-requiring-attention-section/index';
import { StripeEnvironmentBanner } from './stripe-environment-banner';
import { UrgentIssuesBanner } from './urgent-issue-banner';

interface DashboardProps {
  studioData?: StudioDetails;
  isStudioFetching?: boolean;
}

const getPersistedSession = (
  user: UserWithRole | undefined,
): SessionOption | null => {
  if (!user?.selectedSession) return null;

  const { id, name } = user.selectedSession;

  return {
    value: id,
    label: name,
  };
};

export const Dashboard = (_props: DashboardProps) => {
  const [arModalOpen, setArModalOpen] = useState(false);
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<
    SessionOption | undefined | null
  >(null);
  const hasInitializedSessionRef = useRef(false);
  const {
    mutateAsync: updateSelectedSession,
    isPending: isUpdatingSelectedSession,
  } = useUpdateSelectedSession();
  const { data: currentUser } = useGetCurrentUser({ retry: false });

  const { data, isFetching } = useGetAllSessions({
    page: 1,
    limit: 10,
  });

  useEffect(() => {
    if (hasInitializedSessionRef.current) return;
    if (!data?.sessions) return;

    const persistedSession = getPersistedSession(currentUser);

    if (data?.sessions.length) {
      const persistedSessionFromList = data.sessions.find(
        (session) => session._id === persistedSession?.value,
      );

      if (persistedSessionFromList) {
        setSelectedSession({
          label: persistedSessionFromList.name,
          value: persistedSessionFromList._id,
        });
        hasInitializedSessionRef.current = true;
        return;
      }

      if (persistedSession) {
        setSelectedSession(persistedSession);
        hasInitializedSessionRef.current = true;
        return;
      }
    }

    setSelectedSession(ALL_SESSIONS_OPTION);
    hasInitializedSessionRef.current = true;
  }, [currentUser, data?.sessions]);

  const { hasAdminAccess, hasSuperAdminAccess } = useCurrentUserRole();

  // Gate metric widgets on one-time session initialization only. Tying
  // readiness to `isFetching` unmounted the whole metric subtree on every
  // background `useGetAllSessions` refetch (e.g. window refocus), and the
  // stale metric queries then refetched on remount — re-creating the request
  // storm this ticket fixes. `keepPreviousData` keeps the session list stable
  // during background refetches, so the widgets can stay mounted.
  const { apiSessionId, isReady } = useDashboardSessionScope(
    selectedSession,
    hasInitializedSessionRef.current,
  );

  const handleSessionChange = async (
    session: SessionOption | undefined | null,
  ) => {
    try {
      const isAll =
        session == null || session.value === ALL_SESSIONS_ID;
      const nextSession = isAll ? ALL_SESSIONS_OPTION : session;

      setSelectedSession(nextSession);
      await updateSelectedSession({
        selectedSession:
          nextSession != null && nextSession.value !== ALL_SESSIONS_ID
            ? { id: nextSession.value, name: nextSession.label }
            : null,
      });
    } catch (error) {
      console.error(error);
      toast.error('Failed to update selected session');
    }
  };

  const metricsReady = isReady && !!apiSessionId;

  return (
    <div className='flex w-full min-w-0 flex-col'>
      {/* Global TopNavBar + DemoModeBanner mount in `/_auth` (ENR-3525). */}
      <MainLayout>
        <StripeEnvironmentBanner />
        {metricsReady && (
          <DeferredMount>
            <div className='mb-4 w-full min-w-0'>
              <UrgentIssuesBanner sessionId={apiSessionId} />
            </div>
          </DeferredMount>
        )}

        <QuickActionsToolbar />

        <div className='w-full min-w-0 space-y-6 py-6 md:space-y-8 md:py-8'>
          <div className='flex flex-col gap-4 md:flex-row md:items-center'>
            <h2 className='shrink-0 text-sm font-medium uppercase tracking-wide text-slate-600'>
              Studio Metrics
            </h2>
            <div className='hidden h-px flex-1 bg-slate-200 md:block' />
            <div className='w-full md:max-w-[300px]'>
              {isFetching || isUpdatingSelectedSession ? (
                <Loader2 className='size-5 animate-spin' />
              ) : (
                <SessionSelector
                  includeAllSessions
                  session={selectedSession ?? ALL_SESSIONS_OPTION}
                  onChange={(session) => {
                    void handleSessionChange(session);
                  }}
                />
              )}
            </div>
          </div>

          {metricsReady ? (
            <StatsRow sessionId={apiSessionId} />
          ) : (
            <div className='flex justify-center py-12'>
              <Loader2 className='size-8 animate-spin text-muted-foreground' />
            </div>
          )}

          {hasAdminAccess && metricsReady && (
            <LazyDashboardSection className='w-full min-w-0 max-w-full space-y-8'>
              <SectionHeader title='Trends' />
              <div className='grid w-full min-w-0 max-w-full gap-6 lg:grid-cols-2'>
                {hasSuperAdminAccess && (
                  <RevenueTrendChart sessionId={apiSessionId} />
                )}
                <EnrollmentGrowthChart sessionId={apiSessionId} />
              </div>

              <SectionHeader title='Operational Insights' />
              <div className='grid w-full min-w-0 max-w-full gap-6 lg:grid-cols-2'>
                {hasSuperAdminAccess && (
                  <PaymentHealthChart sessionId={apiSessionId} />
                )}
                <ClassPerformanceTabs sessionId={apiSessionId} />
              </div>

              <SectionHeader title='People & Alerts' />
              <div className='grid w-full min-w-0 max-w-full gap-6 lg:grid-cols-3'>
                <div className='min-w-0 max-w-full lg:col-span-3'>
                  <StudentRequiringAttentionSection sessionId={apiSessionId} />
                </div>
              </div>
            </LazyDashboardSection>
          )}
        </div>

        {metricsReady && (
          <AccountReceivableModal
            open={arModalOpen}
            onOpenChange={setArModalOpen}
            sessionId={apiSessionId}
          />
        )}
        <AttendanceDrilldownModal
          isOpen={attendanceModalOpen}
          onClose={() => {
            setAttendanceModalOpen(false);
          }}
        />
      </MainLayout>
    </div>
  );
};
